import { enumValue } from '@miles/prelude/enum'
import { Stack, Typography } from '@mui/material'
import { useTranslation } from 'next-i18next'
import { ReactElement } from 'react'
import { match, P } from 'ts-pattern'
import Bike from 'assets/icons/event/bike.svg'
import Elevation from 'assets/icons/event/elevation.svg'
import Person from 'assets/icons/event/person.svg'
import Pool from 'assets/icons/event/pool.svg'
import Run from 'assets/icons/event/run.svg'
import Walk from 'assets/icons/event/walk.svg'
import {
  DistanceOrElevationInfo,
  DistanceOrElevationTranslationKey,
  DistanceOrElevationUnit
} from 'helpers/raceCategoryLevels'
import {
  CountCourse,
  MultiCourse,
  RaceDetail,
  StepNumber
} from './RaceCard.style'
import RaceCardTime from './RaceCardTime'

interface RaceDataProps {
  raceData: DistanceOrElevationInfo[] | null
  timeZone: string | null
  startDate: Date | null
  forceRow?: boolean
}

interface RelevantInfo {
  icon: ReactElement | null
  isRelevant?: boolean
  elevationSign?: string
}

export const RaceData = ({
  raceData,
  timeZone,
  startDate,
  forceRow
}: RaceDataProps) => {
  const { t } = useTranslation(['common', 'event'])

  const relevantLabels: DistanceOrElevationTranslationKey[] = [
    DistanceOrElevationTranslationKey.running,
    DistanceOrElevationTranslationKey.swimming,
    DistanceOrElevationTranslationKey.walking,
    DistanceOrElevationTranslationKey.biking
  ]
  const relevantCount =
    raceData?.reduce(
      (acc, elem) =>
        relevantLabels.includes(elem.translationKey) ? acc + 1 : acc,
      0
    ) ?? 0

  const { relevant, nonRelevant, stepNumber } = (raceData || []).reduce(
    (acc, elem) => {
      const { elevationSign, icon, isRelevant } =
        match<DistanceOrElevationTranslationKey>(elem.translationKey)
          .returnType<RelevantInfo>()
          .with(enumValue(DistanceOrElevationTranslationKey.running), () => ({
            icon: <Run />,
            isRelevant: true
          }))
          .with(enumValue(DistanceOrElevationTranslationKey.swimming), () => ({
            icon: <Pool />,
            isRelevant: true
          }))
          .with(enumValue(DistanceOrElevationTranslationKey.walking), () => ({
            icon: <Walk />,
            isRelevant: true
          }))
          .with(enumValue(DistanceOrElevationTranslationKey.biking), () => ({
            icon: <Bike />,
            isRelevant: true
          }))
          .with(enumValue(DistanceOrElevationTranslationKey.age), () => ({
            icon: <Person />
          }))
          .with(
            enumValue(DistanceOrElevationTranslationKey.positiveElevation),
            () => ({
              icon: <Elevation />,
              elevationSign: '+'
            })
          )
          .with(
            enumValue(DistanceOrElevationTranslationKey.negativeElevation),
            () => ({
              icon: <Elevation style={{ transform: 'scale(-1, 1)' }} />,
              elevationSign: '-'
            })
          )
          .with(
            P.union(
              enumValue(DistanceOrElevationTranslationKey.runAndBike),
              enumValue(DistanceOrElevationTranslationKey.runAndSwim)
            ),
            () => ({
              icon: null
            })
          )
          .exhaustive()

      if (isRelevant && relevantCount > 1) {
        acc.stepNumber++
      }

      const course =
        elem.unit === DistanceOrElevationUnit.years ||
        (typeof elem.value === 'number' && elem.value > 0) ? (
          <CountCourse key={elem.key}>
            {isRelevant && relevantCount > 1 && (
              <StepNumber $total={relevantCount} $stepNumber={acc.stepNumber}>
                {acc.stepNumber}
              </StepNumber>
            )}
            <RaceDetail>
              {icon}
              {isRelevant && relevantCount > 1 && (
                <Typography variant="bodyLRegular" pr={0.8}>
                  {t(
                    `event:${
                      DistanceOrElevationTranslationKey[elem.translationKey]
                    }` as never
                  )}
                </Typography>
              )}
              <Typography variant="bodyLRegular" color="label.subdued">
                {`${elevationSign ?? ''}${elem.value}`}{' '}
                {elem.unit === DistanceOrElevationUnit.years
                  ? t('years')
                  : elem.unit}
              </Typography>
            </RaceDetail>
          </CountCourse>
        ) : null

      if (isRelevant && relevantCount > 1) {
        acc.relevant.push(course)
      } else {
        acc.nonRelevant.push(course)
      }

      return acc
    },
    {
      stepNumber: 0,
      relevant: [] as Array<ReactElement | null>,
      nonRelevant: [] as Array<ReactElement | null>
    }
  )
  return (
    <>
      {stepNumber > 0 && <MultiCourse>{relevant}</MultiCourse>}
      <Stack
        direction={{ xs: forceRow ? 'row' : 'column', sm: 'row' }}
        flexWrap="wrap"
        gap={2}
      >
        {nonRelevant}
        {!!startDate && (
          <RaceCardTime startDate={startDate} timeZone={timeZone} />
        )}
      </Stack>
    </>
  )
}
